/**
 * Nathalie Robert — weekly contract recap (Monday mornings).
 *
 * Fires every Monday (day-of-week == 1) IF Nathalie has been hired.
 * Pushes a short Messagerie note listing the contracts that need
 * attention this week, grounded in REAL game state (no fictional
 * meetings, no fictional appointments — only contract data the game
 * already tracks):
 *
 *   1. Contracts ending within the next 7 days
 *      (use `daysRemaining` <= 7)
 *   2. Contracts currently UNDERSTAFFED (no vehicle or no driver
 *      assigned) — these are bleeding revenue.
 *   3. Contracts that MISSED deliveries recently (`contractMisses`
 *      counter present on the contract instance).
 *
 * Returns the Messagerie payload OR null when nothing actionable.
 *
 * 31/05/2026 — Nathalie active assistant pattern v2 (recadré sans
 * inventer de RDV).
 */

const TTL_DAYS = 7; // One week — replaced by the next Monday's recap.

const fmtDays = (n) => `${n} jour${n > 1 ? 's' : ''}`;

export function buildNathalieWeeklyRecap(state) {
  if (!state || typeof state !== 'object') return null;
  const hired = !!(state.hiredSupport && state.hiredSupport['role-nathalie-robert']);
  if (!hired) return null;

  const activeContracts = Array.isArray(state.activeContracts) ? state.activeContracts : [];
  if (activeContracts.length === 0) return null;

  // 1. Contracts ending soon (≤ 7 days).
  const endingSoon = activeContracts
    .filter(c => typeof c.daysRemaining === 'number' && c.daysRemaining <= 7 && c.daysRemaining > 0)
    .sort((a, b) => a.daysRemaining - b.daysRemaining);

  // 2. Understaffed contracts (no vehicle OR no driver assigned).
  const understaffed = activeContracts.filter(c => !c.vehicleId || !c.driverId);

  // 3. Contracts with recent missed deliveries.
  const withMisses = activeContracts.filter(c => (c.contractMisses || 0) > 0);

  // If nothing requires attention, stay silent — Nathalie doesn't spam.
  if (endingSoon.length === 0 && understaffed.length === 0 && withMisses.length === 0) {
    return null;
  }

  const blocks = [];

  if (endingSoon.length > 0) {
    blocks.push(`**📅 Contrats arrivant à échéance** (semaine en cours) :`);
    endingSoon.slice(0, 6).forEach(c => {
      blocks.push(`   • ${c.name || c.clientName || 'Contrat'} — encore ${fmtDays(c.daysRemaining)}`);
    });
    if (endingSoon.length > 6) {
      blocks.push(`   • … et ${endingSoon.length - 6} autre${endingSoon.length - 6 > 1 ? 's' : ''}`);
    }
  }

  if (understaffed.length > 0) {
    if (blocks.length > 0) blocks.push('');
    blocks.push(`**⚠️ Contrats sans affectation complète** (perte de revenu en cours) :`);
    understaffed.slice(0, 4).forEach(c => {
      const what = !c.vehicleId && !c.driverId
        ? 'sans véhicule ni chauffeur'
        : !c.vehicleId ? 'sans véhicule' : 'sans chauffeur';
      blocks.push(`   • ${c.name || c.clientName || 'Contrat'} — ${what}`);
    });
    if (understaffed.length > 4) {
      blocks.push(`   • … et ${understaffed.length - 4} autre${understaffed.length - 4 > 1 ? 's' : ''}`);
    }
  }

  if (withMisses.length > 0) {
    if (blocks.length > 0) blocks.push('');
    blocks.push(`**❗ Livraisons manquées récemment** :`);
    withMisses.slice(0, 4).forEach(c => {
      blocks.push(`   • ${c.name || c.clientName || 'Contrat'} — ${c.contractMisses} retard${c.contractMisses > 1 ? 's' : ''}`);
    });
  }

  const intro = `Bonjour Patron,\n\nLe point de la semaine sur vos contrats :\n\n`;
  const outro = `\n\nC'est à vous de voir. Bonne semaine !\n\n— Nathalie`;
  const body = intro + blocks.join('\n') + outro;

  return {
    senderType: 'support',
    senderName: 'Nathalie Robert (Assistante)',
    senderRole: 'role-nathalie-robert',
    tone: 'friendly',
    kind: 'info',
    subject: `Point contrats — semaine ${Math.floor((state.totalDays || 0) / 7) + 1}`,
    body,
    ttlDays: TTL_DAYS,
    receivedDay: state.totalDays || 0,
  };
}
